import {sprintf} from "sprintf";

import brickTypes from "@modules/JourneyBuilder/Bricks/brick-types";
import { filterCheckpoint } from "@modules/JourneyBuilder/Bricks/Checkpoints";
import {prompt, buttonType} from "@modules/JourneyBuilder/Util/prompt";
import loadResourceString from "@core/Util/resources";

import resourceMixin from "@core/Util/Mixins/resources";

import {HelpPopover} from "@modules/JourneyBuilder/Components/Elements";

/**
 * Create an anonymous prompt title component
 * @return {{template: string, components: {HelpPopover}, mixins: *[]}}
 */
function createTitleComponent() {
    return {
        components: { HelpPopover },
        mixins: [resourceMixin],
        template: `
            <div class="d-block w-100">
                <h3 class="d-inline-block">{{resource('JourneyBuilder_Index_PublishJourney_ConfirmationTitle')}}</h3>
<!--                <help-popover :title="resource('JourneyBuilder_Overview_Clone_Prompt_Title')" :content="resource('JourneyBuilder_Overview_Clone_Prompt_Body')"/>-->
            </div>
        `
    };
}

/**
 * Create an anonymous prompt body consisting of the given parts
 * @param { Array<{ title: String, body: String }> } parts
 * @return {Vue}
 */
function createPromptBodyComponent(parts) {
    return {
        props: {
            parts: {
                type: Array,
                default: () => parts
            }
        },
        template: `
            <div>
                <template v-for="(part, index) in parts">
                    <div class="row form-group">
                        <div class="col-12">
                            <h4>
                                {{part.title}}
                            </h4>
                        </div>
                    </div>
                    <div class="row form-group">
                        <div class="col-12">
                            {{part.body}}
                        </div>
                    </div>

                    <div class="row" v-if="index !== parts.length - 1">
                        <div class="col-12">
                            <hr />
                        </div>
                    </div>
                </template>
            </div>
        `
    }
}

/**
 * Confirm whether or not the user wants to publish the given journey
 * @param {JourneyModelBase} journey
 * @returns {Promise}
 */
async function confirmPublish(journey) {
    const hasEmptyAudienceBricks = journey.bricks.some(b => b.type === brickTypes.audience && !b.config.audienceId);
    const hasGoals = journey.bricks.some(b => b.type === brickTypes.goal && b.isActive());
    
    /** @type { Array<{ title: String, body: String }> } */
    const promptBodyParts = [];
    
    if(hasEmptyAudienceBricks) {
        promptBodyParts.push({
            title: loadResourceString('JourneyBuilder_Index_PublishJourney_WithEmptyAudiences_ConfirmationTitle'),
            body: loadResourceString('JourneyBuilder_Index_PublishJourney_WithEmptyAudiences_ConfirmationBody')
        })
    }
    
    if(!hasGoals) {
        promptBodyParts.push({
            title: loadResourceString('JourneyBuilder_Index_PublishJourney_WithoutGoalBrick_ConfirmationTitle'),
            body: loadResourceString('JourneyBuilder_Index_PublishJourney_WithoutGoalBrick_ConfirmationBody')
        });
    }

    //An inactive journey does not need a prompt regarding start dates prior to saving
    //StartDate is used in conjunction with isActive to determine whether records should be processed or not
    if(journey.isActive) {
        if(journey.startDate) {
            promptBodyParts.push({
                title: loadResourceString('JourneyBuilder_Index_PublishJourney_StartDate_ConfirmationTitle'),
                body: sprintf(loadResourceString('JourneyBuilder_Index_PublishJourney_WithStartDate_ConfirmationBody'), journey.startDate)
            })
        } else {
            promptBodyParts.push({
                title: loadResourceString('JourneyBuilder_Index_PublishJourney_WithoutStartDate_ConfirmationTitle'),
                body: loadResourceString('JourneyBuilder_Index_PublishJourney_WithoutStartDate_ConfirmationBody')
            })
        }
    }
    
    const interactionFiltersWithoutDelayMessage = filterCheckpoint.buildInteractionFiltersWithoutDelayMessage(
        journey.bricks.flatMap(b => b.outgoingConnections)
    );
    
    if(interactionFiltersWithoutDelayMessage) {
        promptBodyParts.push({
            title: loadResourceString('JourneyBuilder_FilterSettings_InteractionFilter_NoDelayCheckpoint_ConfirmationTitle'),
            body: interactionFiltersWithoutDelayMessage
        });
    }
    
    //No prompt body parts should imply OK - no peculiarities for the user to worry about
    if(!promptBodyParts.length) {
        return;
    }

    await prompt(
        createTitleComponent(),
        createPromptBodyComponent(promptBodyParts),
        {
            buttons: [
                {
                    ...buttonType.Cancel,
                    isRejection: true
                },
                buttonType.Save
            ]
        }
    );
}

export default confirmPublish;